import {
	anyMultiChoiceFilters,
	bindMultiChoiceGroup,
	clearMultiChoiceInputs,
	setMultiChoiceFromParams,
	updateMultiChoiceBadge,
	updateMultiChoiceCounts
} from './overview-filter-multichoice';
import {updateResultView} from './overview-results';
import {anyTogglesOn, bindToggles, clearToggleInputs, setTogglesFromParams} from './overview-filter-toggle';
import {
	anyHourFilters,
	bindHoursRefresh,
	bindResetButtonsVisibility,
	bindValueGroup,
	clearNumberInputs,
	saveFilterValues,
	setNumbersFromParams,
	setSearchFieldsFromParams,
	updateHoursBadge
} from "./overview-filter-value";
import {bindPopup, popups} from "./overview-filter-popup";
import {bindLocationForValueFilter} from "./overview-filter-location";
import config from './config.json';
import {
	clearFilters,
	getSearchParams,
	loadFromUrl,
	multiChoiceFilters,
	toggleFilters,
	trySaveToUrl,
	valueFilters
} from "./overview-filter-state";
import {attributes} from "./overview-filter-constants";
import {trackVacancySearch} from "./google-analytics";


export const bindFilterButtons = () => {
	bindResetButtonsVisibility();

	bindSearchTriggers();

	bindValueGroup(valueFilters.searchTerm);
	bindValueGroup(valueFilters.location);
	bindValueGroup(valueFilters.radius);
	bindLocationForValueFilter(valueFilters.location);

	for (const popup in popups)
		bindPopup(popups[popup]);

	bindMultiChoiceGroup(multiChoiceFilters.sector, multiChoiceFilters.jobField);
	bindMultiChoiceGroup(multiChoiceFilters.educationLevel);
	bindToggles(toggleFilters.permanentContract);

	bindHoursRefresh();

	bindClearButton("button-clear-filters");

	bindFilterList();

	bindNavigationTriggers();

	updateBadges();
	toggleClearFiltersLink();

	loadFromUrl();
	trackVacancySearch();
}

const updateBadges = () => {
	updateHoursBadge();
	for (const filter in multiChoiceFilters)
		updateMultiChoiceBadge(multiChoiceFilters[filter]);
}

const bindNavigationTriggers = () => {
	const handlePopstate = async () => {
		loadFromUrl();
		setTogglesFromParams();
		setNumbersFromParams();
		setMultiChoiceFromParams();
		setSearchFieldsFromParams();
		await search(false);
	};
	window.addEventListener("popstate", handlePopstate);
}

const bindSearchTriggers = () => {
	const searchButtons = document.querySelectorAll("[data-filter-button-search]");
	searchButtons.forEach((button) => button.addEventListener("click", async () => {
		saveFilterValues();
		trackVacancySearch();
		await search();
	}));

	const enterTriggers = document.querySelectorAll("[data-enter-trigger]");
	enterTriggers.forEach(input => {
		input.addEventListener("keydown", async (event) => {
			if (event.key === "Enter") {
				event.target.blur();
				saveFilterValues();
				trackVacancySearch();
				await search();
			}
		});
	});
}

const bindClearButton = (buttonAttr) => {
	const button = document.querySelector(`button[${buttonAttr}]`);
	button.addEventListener("click", async () => {
		clearFilters();

		// Update filters in the ui
		clearMultiChoiceInputs();
		clearToggleInputs();
		clearNumberInputs();

		await search(true, true);
	});
}

const scrollUp = () => {
	const main = document.querySelector("main");
	const mainTop = main.getBoundingClientRect().top;
	const mainMarginTop = parseInt(getComputedStyle(main).marginTop);
	const nav = document.querySelector("nav").getBoundingClientRect().height;

	window.scrollTo({
		top: mainTop + window.scrollY - nav - mainMarginTop,
		behavior: "smooth"
	});
}

export const search = async (save = true, resetPages = true, updateFilterCounts = true) => {
	// If we don't need to save to the url but we do want to search save should be false
	if (!save || trySaveToUrl(resetPages)) {
		if (updateFilterCounts)
			await fetchFilterCounts();

		await updateResultView();
		scrollUp();

		updateBadges();
	}
}

export const peek = async () => {
	await fetchFilterCounts();
	updateBadges();
}

export const undo = async () => {
	loadFromUrl();
	setMultiChoiceFromParams();
	await peek();
}

const fetchFilterCounts = async () => {
	const response = await fetch(`${config.basepath}/overview/filters?${getSearchParams().toString()}`);

	if (!response.ok)
		return;

	const data = await response.json();

	toggleClearFiltersLink();
	updateMultiChoiceCounts(data.filters);
	updateTotals(data.totalRecords);
}

const toggleClearFiltersLink = () => {
	const clearFiltersElement = document.getElementById("clear-filter-button");

	if (anyHourFilters() || anyMultiChoiceFilters() || anyTogglesOn())
		clearFiltersElement.classList.remove("tw-hidden");
	else
		clearFiltersElement.classList.add("tw-hidden");
}

const updateTotals = (count) => {
	const totals = document.querySelectorAll(`[${attributes.filterShowCount}]`);
	const vacancyTexts = document.querySelectorAll(`[${attributes.filterVacancyAttribute}]`);
	const vacanciesTexts = document.querySelectorAll(`[${attributes.filterVacanciesAttribute}]`);

	totals.forEach(element => element.innerText = count);

	if (count === 1) {
		vacancyTexts.forEach(vacancyText => vacancyText.removeAttribute("hidden"));
		vacanciesTexts.forEach(vacanciesText => vacanciesText.setAttribute("hidden", ""));
	} else {
		vacancyTexts.forEach(vacancyText => vacancyText.setAttribute("hidden", ""));
		vacanciesTexts.forEach(vacanciesText => vacanciesText.removeAttribute("hidden"));
	}
}

const bindFilterList = () => {
	const openButtons = document.querySelectorAll("[data-filter-list-button-open]");
	openButtons.forEach(button => button.addEventListener("click", openFilterList));

	const closeButtons = document.querySelectorAll("[data-filter-list-button-close]");
	closeButtons.forEach(button => button.addEventListener("click", async () => {
		closeFilterList();
		await undo();
	}));

	const confirmButtons = document.querySelectorAll("[data-filter-list-button-confirm]");
	confirmButtons.forEach(button => button.addEventListener("click", async () => {
		closeFilterList();
		await search();
	}));
}

const openFilterList = () => {
	const filterList = document.querySelector(`[${attributes.filterListAttribute}]`);
	if (filterList.classList.contains("tw-left-[-101%]")) {
		filterList.classList.remove("tw-left-[-101%]");
		document.body.classList.add("dg-body-no-scroll");
	}
}

export const closeFilterList = () => {
	const filterList = document.querySelector(`[${attributes.filterListAttribute}]`);

	if (!filterList.classList.contains("tw-left-[-101%]")) {
		filterList.classList.add("tw-left-[-101%]");
		document.body.classList.remove("dg-body-no-scroll");
	}
}

//Script and listener to close filters when screen is resized and larger than 1199px

const toggleFilterListResize = () => {
	if (window.innerWidth >= 1200) {
		const filterList = document.querySelector(`[${attributes.filterListAttribute}]`);

		if (!filterList.classList.contains("tw-left-[-101%]") && document.body.classList.contains("dg-body-no-scroll")) {
			closeFilterList();
		}
	}
}

export const bindWindowResize = () => {
	window.addEventListener("resize", toggleFilterListResize);
}

// Code om de filter popup te sluiten zit generiek in overview-filter-popup.setOpen
